import { Box, Skeleton, Stack, Typography, alpha, Chip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';
import { CRM_PALETTE as P } from '../../../theme/brandPalette';
import { useGetPropertyCardQuery } from '../../../store/api/propertiesApi';

const STATUS_COLOR: Record<string, string> = {
  active:   '#34D399',
  reserved: '#F59E0B',
  sold:     '#60A5FA',
  rented:   '#60A5FA',
  draft:    '#94A3B8',
  inactive: '#64748B',
};

const STATUS_LABEL: Record<string, string> = {
  active:   'Activa',
  reserved: 'Reservada',
  sold:     'Vendida',
  rented:   'Alquilada',
  draft:    'Borrador',
  inactive: 'Inactiva',
};

function fmtPrice(n: number | null | undefined, currency: string): string {
  if (!n) return '—';
  return `${currency} ${n.toLocaleString('es-UY')}`;
}

interface Props {
  /** Código IMP-YYYY-NNNN o Mongo _id */
  propertyId: string;
  /** Destino del tap. Default `/properties/<id>`; para la bolsa, `/bolsa-propiedades/<id>`. */
  to?: string;
}

export function PropertyInlineCard({ propertyId, to }: Props) {
  const golden = P.brand.golden;
  const { data, isLoading, isError } = useGetPropertyCardQuery(propertyId);

  if (isLoading) {
    return (
      <Box
        sx={{
          my: 1, p: 1.5,
          border: `1px solid ${alpha(golden, 0.2)}`,
          borderRadius: 1.5,
          bgcolor: alpha('#141414', 0.5),
          minWidth: 'min(260px, 100%)',
          maxWidth: 340,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="70%" />
            <Skeleton width="50%" />
            <Skeleton width="40%" />
          </Box>
        </Stack>
      </Box>
    );
  }

  if (isError || !data) {
    // Fallback: un link simple para no perder la referencia si la API falla
    return (
      <RouterLink to={to ?? `/properties/${propertyId}`} style={{ color: golden, fontWeight: 600 }}>
        {to ?? `/properties/${propertyId}`}
      </RouterLink>
    );
  }

  // Destino del tap: respeta `to` explícito; si no, propias → /properties, bolsa → /bolsa-propiedades
  // (la ficha propia bloquea con 403 las de otra oficina/tenant).
  const dest = to ?? (data.isPool ? `/bolsa-propiedades/${data.propertyId}` : `/properties/${data.propertyId}`);

  const isRental = data.operation === 'rent' || data.operation === 'temporal_rent';
  const price = isRental ? data.pricing.rentPrice : data.pricing.salePrice;
  const mainImage = data.media?.mainImage ?? undefined;
  const address = [data.location?.address, data.location?.neighborhood ?? data.location?.city]
    .filter(Boolean)
    .join(', ');

  return (
    <Box
      component={RouterLink}
      to={dest}
      sx={{
        display: 'block',
        textDecoration: 'none',
        my: 1, p: 1.5,
        border: `1px solid ${alpha(golden, 0.25)}`,
        borderRadius: 1.5,
        bgcolor: alpha('#141414', 0.7),
        minWidth: 'min(260px, 100%)',
        maxWidth: 340,
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: golden,
          bgcolor: alpha(golden, 0.06),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        {/* Thumb o ícono */}
        <Box
          sx={{
            width: 56, height: 56, borderRadius: 1, flexShrink: 0,
            bgcolor: alpha(golden, 0.1),
            backgroundImage: mainImage ? `url(${mainImage})` : undefined,
            backgroundSize: 'cover', backgroundPosition: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {!mainImage && <HomeIcon sx={{ color: golden, fontSize: 24 }} />}
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.25}>
            <Typography variant="caption" sx={{ color: golden, fontWeight: 700, fontSize: 10, letterSpacing: '0.05em' }}>
              {data.propertyId}
            </Typography>
            <Chip
              label={STATUS_LABEL[data.status] ?? data.status}
              size="small"
              sx={{
                height: 14, fontSize: 9, fontWeight: 700,
                bgcolor: alpha(STATUS_COLOR[data.status] ?? '#888', 0.2),
                color: STATUS_COLOR[data.status] ?? '#888',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <LaunchIcon sx={{ fontSize: 11, color: alpha(golden, 0.5), ml: 'auto' }} />
          </Stack>

          <Typography
            noWrap
            sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 0.25 }}
          >
            {data.title}
          </Typography>

          {address && (
            <Typography noWrap variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block', mb: 0.5 }}>
              {address}
            </Typography>
          )}

          <Typography sx={{ fontSize: 14, fontWeight: 800, color: golden }}>
            {fmtPrice(price, data.pricing?.currency ?? 'USD')}
          </Typography>

          {(data.features?.bedrooms || data.features?.bathrooms || data.features?.totalArea) && (
            <Stack direction="row" spacing={1} mt={0.5} sx={{ color: 'text.disabled' }}>
              {data.features?.bedrooms != null && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <BedIcon sx={{ fontSize: 12 }} />
                  <Typography variant="caption" sx={{ fontSize: 10.5 }}>{data.features.bedrooms}</Typography>
                </Stack>
              )}
              {data.features?.bathrooms != null && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <BathtubIcon sx={{ fontSize: 12 }} />
                  <Typography variant="caption" sx={{ fontSize: 10.5 }}>{data.features.bathrooms}</Typography>
                </Stack>
              )}
              {/* 0 m² es "sin dato", no una superficie: se oculta (QA de Gustavo, 24/09). */}
              {Number(data.features?.totalArea) > 0 && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <SquareFootIcon sx={{ fontSize: 12 }} />
                  <Typography variant="caption" sx={{ fontSize: 10.5 }}>{data.features.totalArea} m²</Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
